import { useEffect, useState } from 'react';

// Counts the person's returns to this tab after a real absence: hidden (another tab, minimized,
// screen locked) for at least AWAY_MS, and no more than once per GAP_MS across all of this
// browser's Ave Q tabs. Losing focus alone is not away — clicking into a card's iframe does that too.
export const AWAY_MS = 30 * 60 * 1000;
export const GAP_MS = 60 * 60 * 1000;
const LAST_KEY = 'aveq:last-return';

let hiddenSince = null;
let pending = false;            // back on screen after a long absence, waiting for focus
let lastReturn = -Infinity;    // this tab's own, for when storage is unavailable
const listeners = new Set();
let installed = false;

// Read every time: another tab may have animated since.
const readLast = () => {
  try { return Number(localStorage.getItem(LAST_KEY)) || -Infinity; } catch (e) { return -Infinity; }
};

function fire(now) {
  pending = false;
  // Nobody here can play it: don't spend the hourly allowance other tabs could use.
  if (!listeners.size) return;
  if (now - Math.max(lastReturn, readLast()) < GAP_MS) return;
  lastReturn = now;
  try { localStorage.setItem(LAST_KEY, String(now)); } catch (e) { /* this tab only then */ }
  listeners.forEach((fn) => fn());
}

function onVisibility() {
  const now = Date.now();
  if (document.visibilityState === 'hidden') {
    if (hiddenSince === null) hiddenSince = now;
    pending = false;
    return;
  }
  const away = hiddenSince === null ? 0 : now - hiddenSince;
  hiddenSince = null;
  if (away < AWAY_MS) return;
  if (document.hasFocus()) fire(now);
  else pending = true;
}

function onFocus() {
  if (pending && document.visibilityState === 'visible') fire(Date.now());
}

function install() {
  if (installed) return;
  installed = true;
  if (document.visibilityState === 'hidden') hiddenSince = Date.now();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onFocus);
}

export function resetReturnsForTests() {
  hiddenSince = null; pending = false; lastReturn = -Infinity; listeners.clear();
  if (installed) {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onFocus);
    installed = false;
  }
}

// How many qualifying returns have happened since the caller mounted. `enabled`: whether this
// caller would act on a return; only those count against the hourly limit.
export default function useReturnAfterAway(enabled = true) {
  const [returns, setReturns] = useState(0);
  useEffect(() => {
    install();
    if (!enabled) return undefined;
    const bump = () => setReturns((n) => n + 1);
    listeners.add(bump);
    return () => { listeners.delete(bump); };
  }, [enabled]);
  return returns;
}
